import { useTranslation } from "react-i18next";

import {
  createErrorResultExt,
  createSuccessResultVoid,
  DirectionEnum,
  DragTypeEnum,
  DropTypeEnum,
  errors,
  getDraggedPassengersInfo,
  getPassengerMoveScopeTranslationKey,
  getPassengerScopeRestrictions,
  INVALID_RIDE_DRAG_STATUSES,
  IRideDto,
  Log,
  mergePassengerScopeRestrictions,
  RIDE_DRAG_TYPES,
  RIDE_DROP_MAX_TIME_DIFFERENCE_MINUTES,
  shouldSkipRideDropWindowOffsetCheck,
  SimpleResult,
  TDragRideDataProcessing,
  timeDiffMinutesCeiling,
  useHasPermissions,
  useProfileAuthorizationCheck,
  useSystemParams,
} from "@ryde/shared";

import { useFixedRidesUtils } from "../fixed-rides";
import {
  IGetIsDropTargetHighlighted,
  IRideDragData,
} from "./drag-handlers.models";

export const useRideIsDroppable = () => {
  const { t } = useTranslation();
  const { isFixedRideMergeAble } = useFixedRidesUtils();
  const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
  const { hasPermissions: hasCreateOtherSitePermission } = useHasPermissions({
    entity: "ride",
    action: "create_other_site",
  });
  const {
    dragTimeRangeMinutes,
    allowDragMergeMultiDestinationRides,
    enableMultiDestination,
  } = useSystemParams();

  const getIsDropTargetHighlighted = ({
    dragItemData,
    overItemData,
    isOver,
    dropAccepted = true,
  }: IGetIsDropTargetHighlighted): boolean => {
    if (
      !dragItemData ||
      dragItemData.type === DragTypeEnum.NOT_DRAGGING ||
      !overItemData ||
      !dropAccepted ||
      !isOver
    ) {
      return false;
    }

    const dragRideDto = dragItemData.payload?.rideDto;
    const dropRideDto = overItemData.rideDrop;
    const isNavbarTarget =
      overItemData.typeDrop === DropTypeEnum.NAV_PLUS_BUTTON;

    if (!dragRideDto?.id || !(isNavbarTarget || dropRideDto?.id)) {
      return false;
    }

    const isDroppableResult = isRideDroppable({
      dragSource: dragItemData,
      dropTarget: overItemData,
    });

    const result = isDroppableResult?.success ? isOver : false;

    return result;
  };

  const getDragSourceStatusError = (
    dragRideDto: Partial<IRideDto>,
  ): SimpleResult<void> | undefined => {
    if (
      !dragRideDto.status ||
      !INVALID_RIDE_DRAG_STATUSES.includes(dragRideDto.status)
    ) {
      return undefined;
    }

    const statusLabel = t(`selectListLabel.${dragRideDto.status}`);
    const errorTranslated = t(errors.ride.cantDragRideWithStatus, {
      status: statusLabel,
    });

    return createErrorResultExt({
      translationKey: errors.ride.cantDragRideWithStatus,
      translationParams: {
        status: statusLabel,
      },
      label: errorTranslated,
    });
  };

  const areRidesCompatible = (
    dragRideDto: Partial<IRideDto>,
    dropRideDto: Partial<IRideDto>,
  ): SimpleResult<void> => {
    if (dragRideDto.id === dropRideDto.id) {
      const errorTranslated = t(errors.ride.cantDropToTheSameRide, {
        id: dropRideDto.id,
      });
      return createErrorResultExt({
        translationKey: errors.ride.cantDropToTheSameRide,
        translationParams: {
          id: dropRideDto.id as number,
        },
        label: errorTranslated,
      });
    }

    if (dragRideDto.direction !== dropRideDto.direction) {
      const errorTranslated = t(
        errors.ride.cantDropToTheRideWithTheOppositeDirection,
        {
          direction: dropRideDto.direction,
        },
      );

      return createErrorResultExt({
        translationKey: errors.ride.cantDropToTheRideWithTheOppositeDirection,
        translationParams: {
          direction: dropRideDto.direction as string,
        },
        label: errorTranslated,
      });
    }

    if (
      !dropRideDto.status ||
      INVALID_RIDE_DRAG_STATUSES.includes(dropRideDto.status)
    ) {
      const errorTranslated = t(errors.ride.cantDropToTheRideWithStatus, {
        status: t(`selectListLabel.${dropRideDto.status}`),
      });

      return createErrorResultExt({
        translationKey: errors.ride.cantDropToTheRideWithStatus,
        translationParams: {
          status: t(`selectListLabel.${dropRideDto.status}`),
        },
        label: errorTranslated,
      });
    }

    if (
      !dragRideDto.rideStart ||
      !dragRideDto.rideEnd ||
      !dropRideDto.rideStart ||
      !dropRideDto.rideEnd
    ) {
      const errorTranslated = t(errors.ride.invalidRideDate);
      return createErrorResultExt({
        translationKey: errors.ride.invalidRideDate,
        label: errorTranslated,
      });
    }

    const rideDropMaxTimeRange =
      dragTimeRangeMinutes || RIDE_DROP_MAX_TIME_DIFFERENCE_MINUTES;
    const shouldCheckRideWindowOffset =
      !shouldSkipRideDropWindowOffsetCheck(dropRideDto);

    if (
      shouldCheckRideWindowOffset &&
      dragRideDto.direction === DirectionEnum.PickUp &&
      timeDiffMinutesCeiling(dragRideDto.rideEnd, dropRideDto.rideEnd) >
        rideDropMaxTimeRange
    ) {
      const errorTranslated = t(
        errors.ride.cantDropToThePickupRideWithEndTimeOffset,
        {
          maxMinutes: rideDropMaxTimeRange,
        },
      );
      return createErrorResultExt({
        translationKey: errors.ride.cantDropToThePickupRideWithEndTimeOffset,
        translationParams: {
          maxMinutes: rideDropMaxTimeRange,
        },
        label: errorTranslated,
      });
    }

    if (
      shouldCheckRideWindowOffset &&
      dragRideDto.direction === DirectionEnum.DropOff &&
      timeDiffMinutesCeiling(dragRideDto.rideStart, dropRideDto.rideStart) >
        rideDropMaxTimeRange
    ) {
      const errorTranslated = t(
        errors.ride.cantDropToTheDropoffRideWithStartTimeOffset,
        {
          maxMinutes: rideDropMaxTimeRange,
        },
      );
      return createErrorResultExt({
        translationKey: errors.ride.cantDropToTheDropoffRideWithStartTimeOffset,
        translationParams: {
          maxMinutes: rideDropMaxTimeRange,
        },
        label: errorTranslated,
      });
    }

    if (
      !enableMultiDestination &&
      !allowDragMergeMultiDestinationRides &&
      !dragRideDto.courseId &&
      !dropRideDto.courseId
    ) {
      if (
        dragRideDto.direction === DirectionEnum.PickUp &&
        dragRideDto.destinationStationId !== dropRideDto.destinationStationId
      ) {
        const errorTranslated = t(
          errors.ride.mergeRidesCanceledDestinationStationsNotMatch,
        );
        return createErrorResultExt({
          translationKey:
            errors.ride.mergeRidesCanceledDestinationStationsNotMatch,
          label: errorTranslated,
        });
      }

      if (
        dragRideDto.direction === DirectionEnum.DropOff &&
        dragRideDto.originStationId !== dropRideDto.originStationId
      ) {
        const errorTranslated = t(
          errors.ride.mergeRidesCanceledOriginStationsNotMatch,
        );
        return createErrorResultExt({
          translationKey: errors.ride.mergeRidesCanceledOriginStationsNotMatch,
          label: errorTranslated,
        });
      }
    }

    return createSuccessResultVoid();
  };

  const isRideDroppable = (params: IRideDragData): SimpleResult<void> => {
    const { dragSource, dropTarget } = params;
    const dragRideDto = dragSource.payload.rideDto;
    const typeDrop = dropTarget.typeDrop;
    const typeDrag = dragSource.type;

    if (typeDrag === DragTypeEnum.STATION) {
      return createSuccessResultVoid();
    }

    if (!dragRideDto.id) {
      const errorTranslated = t(errors.ride.invalidDragRideIdToPerformDragDrop);
      Log.debug(errorTranslated);
      return createErrorResultExt({
        translationKey: errors.ride.invalidDragRideIdToPerformDragDrop,
        label: errorTranslated,
      });
    }

    const draggedPassengers = getDraggedPassengersInfo(
      dragSource as TDragRideDataProcessing,
    );

    const moveScopeTranslationKey = draggedPassengers?.length
      ? getPassengerMoveScopeTranslationKey({
          restrictions: mergePassengerScopeRestrictions(
            draggedPassengers.map(passenger =>
              getPassengerScopeRestrictions(
                passenger,
                profileAuthorizationInCheck,
              ),
            ),
          ),
          hasCreateOtherSite: hasCreateOtherSitePermission,
        })
      : undefined;
    const isSameRideDrop = dropTarget.rideDrop?.id === dragRideDto.id;

    const isLeavingRideDrop =
      typeDrop === DropTypeEnum.NAV_PLUS_BUTTON ||
      (!!dropTarget.rideDrop?.id && !isSameRideDrop);

    if (moveScopeTranslationKey && isLeavingRideDrop) {
      const errorTranslated = t(moveScopeTranslationKey);

      return createErrorResultExt({
        translationKey: moveScopeTranslationKey,
        label: errorTranslated,
      });
    }

    if (RIDE_DRAG_TYPES.includes(typeDrag)) {
      const dragSourceStatusError = getDragSourceStatusError(dragRideDto);

      if (dragSourceStatusError) {
        return dragSourceStatusError;
      }
    }

    if (typeDrop === DropTypeEnum.NAV_PLUS_BUTTON) {
      const info = t(errors.ride.validDropTargetForCreatingNewRide);
      return createSuccessResultVoid(info);
    }

    const dropRideDto = dropTarget.rideDrop;

    if (!dropRideDto?.id) {
      const errorTranslated = t(errors.ride.invalidDropRideIdToPerformDragDrop);
      return createErrorResultExt({
        translationKey: errors.ride.invalidDropRideIdToPerformDragDrop,
        label: errorTranslated,
      });
    }

    const result = areRidesCompatible(dragRideDto, dropRideDto);

    if (!result.success) {
      return result;
    }

    if (dragRideDto.courseId || dropRideDto.courseId) {
      return isFixedRideMergeAble(params);
    }

    return createSuccessResultVoid();
  };

  return { getIsDropTargetHighlighted, isRideDroppable };
};
